import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X, Send, Loader2 } from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { sendChatMessage, type ChatHistoryEntry, type ChatExpenseResult } from '../api/chat.api';
import { formatMoney } from '@/shared/lib/currency';
import { cn } from '@/shared/lib/cn';

interface DisplayMessage {
  role: 'user' | 'assistant';
  content: string;
  expenses?: ChatExpenseResult[];
}

export function AiAssistantWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && messages.length === 0 && user) {
      setMessages([
        {
          role: 'assistant',
          content: `Hello, ${user.firstName}! How can I help you today? Ask me about your expenses, or any finance or tax question.`,
        },
      ]);
    }
  }, [open, messages.length, user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isSending]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;

    const history: ChatHistoryEntry[] = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setIsSending(true);

    try {
      const result = await sendChatMessage(trimmed, history);
      setMessages((prev) => [...prev, { role: 'assistant', content: result.reply, expenses: result.expenses }]);
    } catch (err: any) {
      const message = err?.response?.data?.message || "Sorry, I couldn't reach the assistant. Please try again.";
      setMessages((prev) => [...prev, { role: 'assistant', content: message }]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {open && (
        <div className="flex h-[520px] w-[360px] flex-col overflow-hidden rounded-2xl border bg-card shadow-2xl">
          <div className="flex items-center justify-between border-b bg-primary/5 px-4 py-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold">AI Assistant</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label="Close assistant"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {messages.map((m, i) => (
              <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[85%] rounded-xl px-3 py-2 text-sm',
                    m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
                  )}
                >
                  <p className="whitespace-pre-wrap">{m.content}</p>
                  {m.expenses && m.expenses.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {m.expenses.map((e) => (
                        <button
                          key={e.id}
                          onClick={() => navigate(`/expenses/${e.id}`)}
                          className="block w-full rounded-lg border border-border/60 bg-background/60 px-2 py-1.5 text-left text-xs hover:bg-background"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate font-medium">{e.title}</span>
                            <span className="shrink-0 font-semibold">{formatMoney(e.amount, e.currency)}</span>
                          </div>
                          <div className="mt-0.5 flex items-center gap-1.5 text-muted-foreground">
                            <span>{e.expenseDate}</span>
                            {e.merchantName && <span>· {e.merchantName}</span>}
                            <span>· {e.status}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isSending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-xl bg-muted px-3 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Thinking...
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 border-t p-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your expenses or finance..."
              className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isSending}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground disabled:opacity-40"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105"
        aria-label="Toggle AI assistant"
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>
    </div>
  );
}
